import { useSyncExternalStore } from "react";
import {
  getPenSettings,
  PEN_DASH_PRESETS,
  PEN_STROKE_PRESETS,
  PEN_WIDTH_PRESETS,
  setPenSettings,
} from "../../editor/penSession";
import { penCustomColors } from "../../editor/customColorSwatches";
import { penColorSlots } from "../../editor/colorSlots";
import { BoardColorSlots } from "./BoardColorSlots";

/** Два вида, которые нужны на уроке чаще всего. Точки — в окошке палитры. */
const PEN_DASH_QUICK = [
  { id: "solid" as const, title: "Сплошная линия" },
  { id: "dashed" as const, title: "Пунктир" },
];

function subscribePenSettings(onStoreChange: () => void) {
  const handler = () => onStoreChange();
  window.addEventListener("wb-pen-settings", handler);
  return () => window.removeEventListener("wb-pen-settings", handler);
}

function notifyPenSettings() {
  window.dispatchEvent(new Event("wb-pen-settings"));
}

export function BoardPenOptions() {
  const pen = useSyncExternalStore(subscribePenSettings, getPenSettings, getPenSettings);
  const customColors = useSyncExternalStore(
    penCustomColors.subscribe,
    penCustomColors.get,
    penCustomColors.get
  );
  const slots = useSyncExternalStore(
    penColorSlots.subscribe,
    penColorSlots.get,
    penColorSlots.get
  );
  const activeSlot = useSyncExternalStore(
    penColorSlots.subscribe,
    penColorSlots.getActive,
    penColorSlots.getActive
  );

  function patchPenSettings(partial: Parameters<typeof setPenSettings>[0]) {
    setPenSettings(partial);
    notifyPenSettings();
  }

  return (
    <div className="wb-pen-options" role="group" aria-label="Параметры ручки">
      {/* Цвет и толщина живут в самой точке: на уроке переключаются между
          готовыми перьями, а не настраивают два свойства по отдельности. */}
      <BoardColorSlots
        slots={slots}
        activeColor={pen.stroke}
        activeIndex={activeSlot}
        presets={PEN_STROKE_PRESETS}
        withPastel
        customColors={customColors}
        withWidth
        widthPresets={PEN_WIDTH_PRESETS}
        label="Перья"
        dashes={PEN_DASH_PRESETS}
        onPickDash={(id, index) => {
          // Вид линии принадлежит точке: сменили у красного пера — чёрное
          // осталось сплошным.
          penColorSlots.set(index, { dash: id as "solid" | "dashed" | "dotted" });
          penColorSlots.setActive(index);
          patchPenSettings({ strokeDash: id as typeof pen.strokeDash });
        }}
        onPick={(slot, index) => {
          penColorSlots.setActive(index);
          patchPenSettings({
            stroke: slot.color,
            strokeWidth: slot.width,
            strokeDash: slot.dash,
          });
        }}
        onSetSlot={(index, patch) => penColorSlots.set(index, patch)}
        onAddCustom={(hex) => penCustomColors.add(hex, PEN_STROKE_PRESETS)}
        onRemoveCustom={(hex) => penCustomColors.remove(hex)}
      />

      {/* Сплошная и пунктир — одним нажатием, без клавиатуры.
          Клавиша D делает то же, но на ноутбуке Windows глушит касание
          тачпада сразу после нажатия клавиши, и первый штрих пропадал. */}
      <div className="wb-pen-dash-row" role="group" aria-label="Вид линии">
        {PEN_DASH_QUICK.map((d) => {
          const active = (slots[activeSlot]?.dash ?? "solid") === d.id;
          return (
            <button
              key={d.id}
              type="button"
              className={`wb-pen-dash-row__btn${active ? " is-active" : ""}`}
              title={d.title}
              aria-label={d.title}
              aria-pressed={active}
              onClick={() => {
                penColorSlots.set(activeSlot, { dash: d.id });
                patchPenSettings({ strokeDash: d.id });
              }}
            >
              <span className={`wb-pen-dash-row__line is-${d.id}`} aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
